import { Component, signal, input, output, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { AddNewValue } from "../add-new-value/add-new-value";
import { AttributesService } from '../../../../services/productAttributes/productAttributes.service';
import { forkJoin } from 'rxjs';
import { 
  ProductAttributeItemResponse, 
  ProductAttributeValueResponse 
} from '../../../../services/productAttributes/productAttributes.model';

@Component({
  selector: 'app-edit-attribute',
  standalone: true,
  imports: [FormsModule, AddNewValue],
  templateUrl: './edit-attribute.html'
})
export class EditAttribute implements OnInit {
  private attributesService = inject(AttributesService);

  productId = input.required<number>(); 
  initialAttribute = input<ProductAttributeItemResponse | null>(null); 
  onClose = output<void>();

  newAttributeKeyEn = signal<string>('');
  newAttributeKeyAr = signal<string | null>(null);
  tempValues = signal<ProductAttributeValueResponse[]>([]);
  
  isModalOpen = signal<boolean>(false);
  isSaving = signal<boolean>(false);
  arTouched = signal<boolean>(false);
  editData = signal<{ en: string; ar: string | null; index?: number } | null>(null);
  editingIndex = signal<number | null>(null);

  ngOnInit() {
    const attr = this.initialAttribute();
    if (attr) {
      this.newAttributeKeyEn.set(attr.attributeKey.titleEn || '');
      this.newAttributeKeyAr.set(attr.attributeKey.titleAr);
      this.tempValues.set([...attr.attributeValues]);
    }
  }

  openAddModal() {
    this.editingIndex.set(null);
    this.editData.set(null);
    this.isModalOpen.set(true);
  }

  openEditModal(index: number) {
    const val = this.tempValues()[index];
    this.editingIndex.set(index);
    this.editData.set({ en: val.titleEn || '', ar: val.titleAr, index });
    this.isModalOpen.set(true);
  }

  handleValueAdded(value: { en: string; ar: string | null; id?: number }) {
    const formattedValue: ProductAttributeValueResponse = {
      id: value.id,
      titleEn: value.en,
      titleAr: value.ar || null
    };
    if (this.editingIndex() !== null) {
      this.tempValues.update(vals => {
        const newVals = [...vals];
        newVals[this.editingIndex()!] = formattedValue;
        return newVals;
      });
    } else {
      this.tempValues.update(vals => [...vals, formattedValue]);
    }
    this.isModalOpen.set(false);
  }

  removeValue(index: number, event: Event) {
    event.stopPropagation();
    this.tempValues.update(vals => vals.filter((_, i) => i !== index));
  }

  updateAttribute() {
    const attr = this.initialAttribute();
    if (!attr || this.isSaving()) return;
    this.isSaving.set(true);

    const productId = this.productId();
    const keyId = attr.attributeKey.id;

    const updateKeyReq = this.attributesService.updateAttributeKey(productId, keyId, {
      titleEn: this.newAttributeKeyEn().trim() || undefined,
      titleAr: this.newAttributeKeyAr()!.trim()
    });

    const updateValuesReqs = this.tempValues()
      .filter(v => v.id)
      .map(v => this.attributesService.updateAttributeValue(productId, keyId, v.id!, {
        titleEn: v.titleEn || undefined,
        titleAr: v.titleAr as string
      }));

    forkJoin([updateKeyReq, ...updateValuesReqs]).subscribe({
      next: () => {
        this.isSaving.set(false);
        this.onClose.emit();
      },
      error: () => this.isSaving.set(false)
    });
  }

  goBack() {
    this.onClose.emit();
  }
}